import React, { useEffect, useState } from 'react'
import gql from 'graphql-tag'
import GridBase, { GridOptions } from '../../components/General/GridBase/GridBase'
import BasisTypeUpdatePricesModal from '../../components/AppliedModals/BasisTypesUpdateModal/BasisTypesUpdatePricesModal'
import { useAllBasisTypesQuery, AllBasisTypeListingFragment, useDeleteBasisTypeMutation, BasisTypeSortInput, BasisTypeFilterInput, SortEnumType, AllBasisTypesQueryVariables } from '../../generated/graphql'
import Button, { ButtonType } from '../../components/General/Button/Button'
import GeneralPage from '../../components/General/GeneralPage/GeneralPage'

import ellipsish from '../../icons/ellipsis-h.svg'
import Modal from '../../components/Modal/Modal'
import moment from 'moment'
import Tile from '../../components/General/Tile/Tile'
import HookedCheckbox from '../../components/General/Inputs/HookedCheckbox'
import { NavigateFunction, useNavigate } from 'react-router-dom'

export const BasisTypeFragment = gql`
    fragment allBasisTypeListing on BasisType {
        basisTypeKey
        description
        name
        isArchived
        currentPrice {
            price
            effectiveDate
        }
    }
`

export const BasisTypesQuery = gql`
    query AllBasisTypes($sortObject: [BasisTypeSortInput!], $filterObject: BasisTypeFilterInput) {
        basisTypeGetAll(order: $sortObject, where: $filterObject) {
           ...allBasisTypeListing
        }
    }
`
export const DeleteBasisTypeMutation = gql`
    mutation DeleteBasisType($id: Int!) {
        basisTypeDelete(id: $id)
    }
`

interface BasisTypeDataWrapperProps {
    currentSort: BasisTypeSortInput

    editBasisType(basisType: AllBasisTypeListingFragment)
    deleteBasisType(basisType: AllBasisTypeListingFragment)
    currentlyDeletingBasisTypes?: AllBasisTypeListingFragment

    currentFilter: BasisTypeFilterInput

    updateClosed: Boolean
}

const BasisTypeDataWrapper = (props: BasisTypeDataWrapperProps): JSX.Element => {

    const variables: AllBasisTypesQueryVariables = {
        sortObject: props.currentSort,
        ...(Object.keys(props.currentFilter).length && { filterObject: props.currentFilter })
    }

    const { data, loading, error, refetch } = useAllBasisTypesQuery({ variables })

    const [deleteMutation] = useDeleteBasisTypeMutation()

    const confirmDelete = (id: number) => {
        deleteMutation({ variables: { id } })
            .then(() => props.deleteBasisType(null))
    }

    useEffect(() => {
        if (!props.updateClosed) {
            refetch()
        }
    }, [props.updateClosed, refetch])

    const cancelDelete = () => {
        props.deleteBasisType(null)
    }

    const gridOptions: GridOptions<AllBasisTypeListingFragment> = {
        columnDefs: [
            { headerName: "Basis Type", field: "name", dataTransform: o => o.name },
            { headerName: "Description", field: "description", dataTransform: o => o.description },
            { headerName: "Price", field: "price", dataTransform: o => o.currentPrice.price.toFixed(4) },
            { headerName: "Effective Date", field: "date", dataTransform: o => moment.utc(o.currentPrice.effectiveDate).format('LL') }
        ],
        isErrored: !!error,
        isLoading: loading,
        rowData: (loading || error) ? [] : data.basisTypeGetAll,
        rowActions: [
            {
                icon: ellipsish, items: [
                    { displayName: "Edit", action: props.editBasisType },
                    { displayName: "Delete", action: props.deleteBasisType }
                ]
            }
        ],
        dataKeyColumn: "basisTypeKey",
        linkToPath: "/pricing/basis-types/show/:key"
    }

    return (
        <div>
            <GridBase<AllBasisTypeListingFragment> gridOptions={gridOptions} />
            {!!props.currentlyDeletingBasisTypes && <Modal headerText="Delete?">
                <p>Once you delete this Basis Type, it will not be able to be reversed.</p>
                <p>Basis Type: {props.currentlyDeletingBasisTypes.name}</p>
                <button onClick={cancelDelete}>Cancel</button> <button onClick={() => confirmDelete(props.currentlyDeletingBasisTypes.basisTypeKey)}>Delete</button>
            </Modal>}
        </div>
    )
}

const BasisTypeList = () => {
    const navigate = useNavigate()
    const [currentSort, setCurrentSort] = useState<BasisTypeSortInput>({ name: SortEnumType.Asc })
    const [currentFilter, setCurrentFilter] = useState<BasisTypeFilterInput>({ isArchived: { eq: false } })
    const [currentlyDeletingBasisTypes, setCurrentlyDeletingBasisTypes] = useState<AllBasisTypeListingFragment | undefined>()
    const [updateAll, setUpdateAll] = useState<boolean>(false)

    const navigateToNewBasisType = () => {
        navigate('/pricing/basis-types/new')
    }

    const editBasisType = (basisType: AllBasisTypeListingFragment) => {
        navigate(`/pricing/basis-types/edit/${basisType.basisTypeKey}`)
    }

    const deleteBasisTypes = (basisType: AllBasisTypeListingFragment) => {
        setCurrentlyDeletingBasisTypes(basisType)
    }

    const updateAllBasisTypes = () => {
        setUpdateAll(true)
    }

    const closeUpdateAll = () => {
        setUpdateAll(false)
    }

    const setShowArchived = (showArchived: boolean) => {
        const newFilter = Object.assign({}, currentFilter)
        newFilter.isArchived = { eq: showArchived }
        setCurrentFilter(newFilter)
    }

    return (
        <GeneralPage title="Basis Types" headerContent={
            <div>
                <HookedCheckbox
                    label="Show Archived"
                    defaultValue={false}
                    propertyKey={"showArchived"}
                    errors={{}}
                    register={React.createRef()}
                    onChange={(event) => setShowArchived(event.target.checked)}
                    inline={true}
                />
                <Button clickAction={updateAllBasisTypes} buttonType={ButtonType.Success}>Update All</Button>
                <Button clickAction={navigateToNewBasisType}>New Basis Type</Button>
            </div>
        }>
            <Tile>
                <BasisTypeDataWrapper
                    currentSort={currentSort}
                    currentlyDeletingBasisTypes={currentlyDeletingBasisTypes}
                    deleteBasisType={deleteBasisTypes}
                    currentFilter={currentFilter}
                    editBasisType={editBasisType}
                    updateClosed={updateAll} />
            </Tile>
            {updateAll && <BasisTypeUpdatePricesModal
                closeModal={closeUpdateAll}
            />}
        </GeneralPage>
    )
}


export default BasisTypeList